/**
 * Plays an outage announcement in the workspace's CONFIGURED phone voice (POST /api/voices/sample,
 * which is not demo-gated — a preview never reaches a caller). If the voice provider is unavailable
 * (e.g. an ElevenLabs billing hold returns 401/402, or the caller lacks policy.write to synthesize),
 * it degrades to the browser's device voice, clearly labelled, rather than erroring or pretending the
 * robot voice is the real one. Doctrine 9: degrade gracefully; never mislead about what was heard.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api.ts";
import { usePermissions } from "../../lib/useApi.ts";
import { Icon } from "./ui";

type Mode = "idle" | "loading" | "playing" | "device" | "error";

export function AnnouncementPlayer({ text, compact = false }: { text: string; compact?: boolean }) {
  const { can } = usePermissions();
  const maySynthesize = can("policy.write");
  const [mode, setMode] = useState<Mode>("idle");
  const [note, setNote] = useState("");
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);

  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      if (typeof window !== "undefined" && "speechSynthesis" in window) window.speechSynthesis.cancel();
    },
    [],
  );

  function stop() {
    audioRef.current?.pause();
    if (typeof window !== "undefined" && "speechSynthesis" in window) window.speechSynthesis.cancel();
    setMode("idle");
  }

  function speakDevice(reason: string) {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) {
      setMode("error");
      setNote("No voice preview is available on this device. The wording is shown in full above.");
      return;
    }
    const u = new SpeechSynthesisUtterance(text);
    u.onend = () => setMode("idle");
    u.onerror = () => setMode("error");
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
    setMode("device");
    setNote(reason);
  }

  async function play() {
    if (mode === "playing" || mode === "device" || mode === "loading") {
      stop();
      return;
    }
    setNote("");
    if (!maySynthesize) {
      speakDevice("Device voice — hearing the actual phone voice needs the Configure permission.");
      return;
    }
    setMode("loading");
    try {
      const url = await api.voices.sample(undefined, undefined, text);
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = url;
      const a = audioRef.current ?? new Audio();
      audioRef.current = a;
      a.src = url;
      a.onended = () => setMode("idle");
      a.onerror = () => speakDevice("Playback failed — using a device preview instead.");
      await a.play();
      setMode("playing");
      setNote("Your configured phone voice — this is what callers hear.");
    } catch {
      speakDevice("Configured voice is unavailable right now — playing a device preview instead.");
    }
  }

  const busy = mode === "loading";
  const active = mode === "playing" || mode === "device";
  return (
    <div className="audio-preview">
      <button
        className="play-button"
        aria-label={active ? "Stop announcement preview" : "Play the announcement"}
        onClick={() => void play()}
        disabled={busy}
      >
        <Icon name={busy ? "loader" : active ? "pause" : "play"} className={busy ? "spin" : ""} size={compact ? 21 : 25} />
      </button>
      {note && (
        <small role="status" className="muted" style={{ maxWidth: 260 }}>
          {note}
        </small>
      )}
    </div>
  );
}
